"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-[6px] text-tebox-body font-medium font-sans transition-[background-color,box-shadow,transform,opacity] focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 hover:translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[0_0_0_1px_#000000,-2px_2px_0_0_#000000] active:translate-x-0 active:translate-y-0 active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:!text-primary-foreground focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:!text-destructive-foreground focus-visible:ring-destructive/20 focus-visible:ring-[3px] focus-visible:outline-1",
        outline:
          "border border-input bg-background hover:bg-accent hover:!text-accent-foreground focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:!text-secondary-foreground focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1",
        ghost:
          "hover:bg-accent hover:!text-accent-foreground focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1",
        link: "text-primary underline-offset-4 hover:underline hover:shadow-none hover:translate-x-0 hover:translate-y-0 focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-[6px] px-3 text-tebox-caption",
        lg: "h-10 rounded-[6px] px-8",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
